import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from '@modules/shared/components/svg-icon/svg-icon.component';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { 
  BehaviorSubject, combineLatest, distinctUntilChanged, EMPTY, filter, from, map, of, skip, Subject, switchMap,
  takeUntil, tap 
} from 'rxjs';
import { SelectedStopsComponent } from './components/selected-stops/selected-stops.component';
import { LineSearchResult, StopSearchResult } from './interfaces';
import { TrafficDeviationSearchService } from '../../services/traffic-deviation-search.service';
import { TrafficDeviationSearchStore } from '../../stores/traffic-deviation-search.store';
import { TrafficDeviationService } from '@components/traffic-deviations/services/traffic-deviation.service';
import { SelectedLineComponent } from "./components/selected-line/selected-line.component";
import { SpinnerComponent } from '@modules/shared/components';
import { ClickOutsideDirective } from '@modules/shared/directives/click-outside.directive';
import { ClickStopPropagationDirective } from '@modules/shared/directives/click-stop-propagation.directive';
import { ValidationMessageComponent } from '@components/traffic-deviations/components/validation-message/validation-message.component';

@Component({
    selector: 'app-search-stop',
    imports: [
        CommonModule, TranslateModule, SvgIconComponent, ReactiveFormsModule, SelectedStopsComponent,
        SelectedLineComponent, SpinnerComponent, ClickOutsideDirective, ClickStopPropagationDirective,
        ValidationMessageComponent
    ],
    templateUrl: './search-stop.component.html',
    styleUrls: ['./search-stop.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchStopComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchResultsContainer') searchResultsContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(SelectedStopsComponent) selectedStopsComponent!: SelectedStopsComponent;
  
  private readonly _elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly _searchStore = inject(TrafficDeviationSearchStore);
  private readonly _searchService = inject(TrafficDeviationSearchService);
  private readonly _deviationService = inject(TrafficDeviationService);
  private readonly _cdr = inject(ChangeDetectorRef);
  
  protected readonly search = new FormControl<string | undefined>(undefined);
  protected readonly affectedControl = new FormControl<null>(null);

  protected get shouldShowAffectedError(): boolean {
    const routeInfo = this._deviationService.trafficDeviationForm.controls.affected.controls.routeInfo;
    const hasRouteInfoInteraction = this.hasTouchedOrDirtyControl(routeInfo);
    return this.affectedControl.invalid && hasRouteInfoInteraction;
  }
  protected readonly stopSearchResults$ = new BehaviorSubject<ReadonlyArray<StopSearchResult>>([]);
  protected readonly lineSearchResults$ = new BehaviorSubject<ReadonlyArray<LineSearchResult>>([]);
  protected readonly searchLoader$ = new BehaviorSubject<boolean>(false);
  protected readonly lineStopSearchLoader$ = new BehaviorSubject<boolean>(false);
  protected readonly choosenStops$ = this._searchService.choosenStops$.asObservable();
  protected readonly existingTrafficDeviationLoaded$ = 
    this._searchService.existingTrafficDeviationLoaded$;

  private readonly _destroy$ = new Subject<void>();
  readonly standaloneStatusChanges$ = new Subject<void>();
  private _unfilteredStopResults: ReadonlyArray<StopSearchResult> = [];
  private _currentStopResults: StopSearchResult[] = [];
  private _keydownListener: ((event: KeyboardEvent) => void) | null = null;
  private _resultsManuallyDismissed = false;
  private _currentSearchQuery = '';

  private hasTouchedOrDirtyControl(control: AbstractControl): boolean {
    if (control.touched || control.dirty) {
     return true;
    }

    if (control instanceof FormGroup) {
     return Object.values(control.controls).some((child) => this.hasTouchedOrDirtyControl(child));
    }

    if (control instanceof FormArray) {
     return control.controls.some((child) => this.hasTouchedOrDirtyControl(child));
    }

    return false;
  }

  ngOnInit(): void {
    combineLatest([
      this._searchService.choosenStops$,
      this._searchService.choosenLines$
    ]).pipe(
      takeUntil(this._destroy$)
    ).subscribe(([stops, lines]) => {
      if (stops.length > 0 || lines.length > 0) {
        this.affectedControl.setErrors(null);
      } else {
        this.affectedControl.setErrors({ noAffectedSelection: true });
      }
    });

    this._deviationService.trafficDeviationForm.controls.affected.controls.routeInfo.events.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this._cdr.markForCheck();
    });

    this.affectedControl.events.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this._cdr.markForCheck();
    });

    // Subscribe to route updates and refresh displayed results with updated data
    this._searchService.stopSearchResultUpdates$.pipe(
      takeUntil(this._destroy$)
    ).subscribe((updatedResults) => {
      // Don't show results if user manually dismissed them
      if (this._resultsManuallyDismissed) {
        return;
      }
      
      // Check if these updates contain any items we're currently displaying
      // This handles the case where "ko" ByPlaceId calls complete after filtering to "kokoskakan"
      // Include hastusRegion as part of stop identity since placeId alone is not unique across regions
      const currentStopIdentities = new Set(
        this._currentStopResults.map(r => `${r.placeId}|${r.hastusRegion}`)
      );
      const currentQuery = (this.search.value || '').trim().toLowerCase();
      
      // Filter updates to only those matching current display AND current search query
      const relevantUpdates = updatedResults.filter(update => {
        // Must be in current displayed results (matching both placeId and hastusRegion)
        const updateIdentity = `${update.placeId}|${update.hastusRegion}`;
        if (!currentStopIdentities.has(updateIdentity)) {
          return false;
        }
        // Must also match current search query text
        if (currentQuery && !update.description.toLowerCase().includes(currentQuery)) {
          return false;
        }
        return true;
      });
      
      if (relevantUpdates.length === 0) {
        // No relevant updates for current results
        return;
      }
      
      // Verify search input hasn't changed since last search
      if (currentQuery && currentQuery !== this._currentSearchQuery) {
        return;
      }
      
      // Update current results by merging in the updated data (routes, loaders)
      const updatedCurrentResults = this._currentStopResults.map(current => {
        const update = relevantUpdates.find(u => 
          u.placeId === current.placeId && u.hastusRegion === current.hastusRegion
        );
        if (update) {
          // Merge the update into current result
          return { ...current, effectedRoutes: update.effectedRoutes, stopSearchLoader: update.stopSearchLoader };
        }
        return current;
      });
      
      this._currentStopResults = updatedCurrentResults;
      
      // Apply an additional safety filter to ensure only results matching current search are displayed
      const currentSearchText = (this.search.value || '').trim().toLowerCase();
      let resultsToDisplay = updatedCurrentResults;
      if (currentSearchText) {
        resultsToDisplay = updatedCurrentResults.filter(result => 
          result.description.toLowerCase().includes(currentSearchText)
        );
      }
      
      // Reapply region filter if needed and refresh display
      const selectedRegion = this._searchStore.stateSnapshot.selectedHastusRegion;
      if (selectedRegion) {
        const filteredResults = resultsToDisplay.filter((result) => {
          return result.hastusRegion === selectedRegion;
        });
        this.stopSearchResults$.next(filteredResults);
      } else {
        this.stopSearchResults$.next(resultsToDisplay);
      }
    });

    this._searchService.postQuery(this.search).pipe(
      skip(1),
      tap((query) => {
        // Track current query for stale result detection
        this._currentSearchQuery = query?.trim().toLowerCase() || '';
        // Clear previous results
        this.stopSearchResults$.next([]);
        this.lineSearchResults$.next([]);
        // Only reset dismiss flag if user is actually typing a new search (not when clearing)
        if (query?.trim()) {
          this._resultsManuallyDismissed = false;
        }
      }),
      filter((query): query is string => {
        // Don't clear cache when field is empty - preserve it for reuse
        // Cache is cleared by service when garage/date/time changes
        return !!query;
      }),
      map((query) => {
        return this._searchService.handleQueryBeforeRequest(query);
      }),
      switchMap((query) => {
        if(!query) return EMPTY;
        
        // Check if we can filter from cache instead of making a new API call
        if (this._searchService.canFilterFromCache(query)) {
          this.searchLoader$.next(false);
          const filteredResults = this._searchService.filterCachedResults(query);
          // Wrap with metadata to indicate these are from cache
          return of({ results: filteredResults, fromCache: true, query });
        }
        
        this.searchLoader$.next(true);
        return from(
          this._searchService.postStopsOrRoutesRequest(query).then((response) => ({
            results: response,
            fromCache: false,
            query
          }))
        );
      }),
      switchMap(async (response: { results: any, fromCache: boolean, query: string }) => {
        // Use the query that produced the response, not the current search value
        // This prevents race conditions where the user types new text while waiting for a response
        const processedResults = await this._searchService.handleQueryResponse(
          response.results, 
          response.query, 
          response.fromCache
        );
        // Return both results and the query that produced them
        return { results: processedResults, query: response.query };
      }),
      takeUntil(this._destroy$)
    ).subscribe(({ results: searchResults, query }) => {
      // Ignore stale results - only process if this query still matches current input
      // This prevents race conditions where slower requests finish after faster ones
      // Note: Don't trim when comparing - stop point queries have significant trailing spaces
      const currentSearchValue = this.search.value || '';
      if (query !== currentSearchValue.trim() && query !== currentSearchValue) {
        // Clear loader for stale results
        this.searchLoader$.next(false);
        return;
      }
      
      // Don't show results if user manually dismissed them
      if (this._resultsManuallyDismissed) {
        this.searchLoader$.next(false);
        return;
      }
      
      this.searchLoader$.next(false);
      if (!searchResults?.length) return;
      if ('placeId' in searchResults[0]) {
        const stopResults = <StopSearchResult[]>searchResults;
        this._unfilteredStopResults = stopResults;
        this._currentStopResults = stopResults;
        this._currentSearchQuery = query.toLowerCase();
        
        // If a region is already selected, filter new results to that region
        const selectedRegion = this._searchStore.stateSnapshot.selectedHastusRegion;
        if (selectedRegion) {
          const filteredResults = stopResults.filter((result) => {
            return result.hastusRegion === selectedRegion;
          });
          this.stopSearchResults$.next(filteredResults);
        } else {
          this.stopSearchResults$.next(stopResults);
        }
      } else {
        this.lineSearchResults$.next(<LineSearchResult[]>searchResults);
      }

      this._handleAccessibility();
    });

    this._searchService.focusSearchField$.pipe(
      takeUntil(this._destroy$) 
    ).subscribe(() => document.getElementById('search')?.focus());

    this._searchStore.getSelectedStopSearches().pipe(
      distinctUntilChanged(),
      takeUntil(this._destroy$)
    ).subscribe((selectedStops) => {
      if (!selectedStops?.length) {
        this._searchService.choosenStops$.next([]);
        this._searchService.setHastusRegion();
        return;
      }

      this._searchService.setHastusRegion(selectedStops[0].hastusRegion);
      const choosenStops = this._searchService.mapSelectedStopsToChoosenStops(selectedStops);
      this._searchService.choosenStops$.next(structuredClone(choosenStops));
    });

    this._deviationService.deviationTypeOptions.valueChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.stopSearchResults$.next([]);
      this.lineSearchResults$.next([]);
      this._searchService.selectedLineInBlade$.next(undefined);
      this._searchService.choosenLines$.next([]);
      this._searchStore.updateSearchState({ selectedStopSearches: [] });
    });
  }

  ngAfterViewInit(): void {
    this.selectedStopsComponent?.standaloneStatusChanges$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.standaloneStatusChanges$.next());
  }

  ngOnDestroy(): void {
    // Remove keydown listener if it exists
    if (this._keydownListener && this.searchResultsContainer?.nativeElement) {
      this.searchResultsContainer.nativeElement.removeEventListener('keydown', this._keydownListener);
    }
    this._destroy$.next();
    this._destroy$.complete();
  }
  
  protected onSearchInputKeyDown(event: KeyboardEvent): void {
    const firstResult = this.stopSearchResults$.value[0];
    if (firstResult) {
      const checkboxId = this.getResultCheckboxId(firstResult);
      const checkboxElement = document.getElementById(checkboxId);
      if (checkboxElement) checkboxElement.focus();
    }
    
    event.preventDefault();
  }

  protected async onClickToggleLineSearchResult(
    selectedLine: LineSearchResult
  ): Promise<void> {
    if (selectedLine.lineStopSearchLoader) return;
    selectedLine.lineStopSearchLoader = true;
    await this._searchService.toggleLineSearchResult(selectedLine); 
    selectedLine.lineStopSearchLoader = false;
    this.closeSearchResults();
  }
  
  protected async onClickToggleStopSearchResult(selectedStop: StopSearchResult): Promise<void> {
    if (selectedStop.stopSearchLoader) return;
    selectedStop.stopSearchLoader = true;
    await this._searchService.toggleStopSearchResult(selectedStop);
    selectedStop.stopSearchLoader = false;

    const selectedRegion = this._searchStore.stateSnapshot.selectedHastusRegion;
    
    // If stop was selected, filter results to only show stops from the same region
    if (selectedStop.selected && selectedRegion) {
      const filteredResults = this._unfilteredStopResults.filter((result) => {
        return result.hastusRegion === selectedRegion;
      });
      this.stopSearchResults$.next(filteredResults);
    }
    
    // If stop was deselected and no region is selected anymore, restore original unfiltered results
    if (!selectedStop.selected && !selectedRegion) {
      this.stopSearchResults$.next(this._unfilteredStopResults);
    }
  }
  
  protected selectSingleResult(): void {
    const stopSearchResults = this.stopSearchResults$.value;
    if (stopSearchResults?.length === 1) {
      this.search.setValue(stopSearchResults[0].description);
    }
  }

  protected closeSearchResults(): void {
    this._resultsManuallyDismissed = true;
    this.stopSearchResults$.next([]);
    this.search.setValue(undefined);
  } 

  protected closeLineSearchResults(): void {
    this._resultsManuallyDismissed = true;
    this.lineSearchResults$.next([]);
    this.search.setValue(undefined);
  }

  protected trackByKey(key: string, index: number, item: unknown): string | number {
    return (item as Record<string, unknown>)[key] as string || index;
  }

  protected trackByStopIdentity(index: number, item: StopSearchResult): string {
    return `${item.type}:${item.hastusRegion}:${item.placeId}`;
  }

  protected getResultCheckboxId(result: StopSearchResult): string {
    // Build DOM id from the same unique identity used by trackByStopIdentity
    return `result-${result.type}-${result.hastusRegion}-${result.placeId}`;
  }

  private _handleAccessibility(): void {
    setTimeout(() => {
      // Remove previous listener if it exists
      if (this._keydownListener && this.searchResultsContainer?.nativeElement) {
        this.searchResultsContainer.nativeElement.removeEventListener('keydown', this._keydownListener);
      }

      // Create and store new listener
      this._keydownListener = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          this._onEnterSelectSearchResult(' + .result');
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          this._onArrowUppOrDownKeyNavigation(event.key, '.search-results input');
        }
        if (event.key === 'F2') {
          event.stopPropagation();
          document.getElementById('search')?.focus();
        }
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.closeSearchResults();
        }
      };

      // Add the new listener
      this.searchResultsContainer?.nativeElement.addEventListener('keydown', this._keydownListener);
    }, 100)
  }

  private _onEnterSelectSearchResult(selector: string): void {
    const activeElement = document.activeElement;
    if (
      activeElement 
      && activeElement instanceof HTMLInputElement 
      && activeElement.type === 'checkbox'
    ) {
      const searchResult = this._elementRef.nativeElement.querySelector<HTMLDivElement>(
        `#${CSS.escape(activeElement.id)}${selector}`);
      searchResult?.click();
    }
  }

  private _onArrowUppOrDownKeyNavigation(downOrUp: 'ArrowDown' | 'ArrowUp', selector: string): void {
    const inputsNodes = this._elementRef.nativeElement.querySelectorAll(selector);
    if (!inputsNodes) return;

    const inputs = Array.from(inputsNodes) as HTMLInputElement[];
    const activeElement = document.activeElement;
    if (!activeElement || !inputs) return;
    if (activeElement instanceof HTMLInputElement && activeElement.type === 'checkbox') {
      const indexOfActiveElement = inputs.indexOf(activeElement);
      let nextElementOfFocus: number;
      if (indexOfActiveElement !== -1) {
        switch (downOrUp) {
          case 'ArrowDown':
            nextElementOfFocus = (indexOfActiveElement+1) % inputs.length;
            break;
          case 'ArrowUp':
            nextElementOfFocus = (inputs.length+indexOfActiveElement-1) % inputs.length;
          break;
        }
        inputs[nextElementOfFocus].focus();
      }
    }
  }

  public markReplaceStopsAsTouched(): void {
    this.selectedStopsComponent?.markReplaceStopsAsTouched();
  }

  public hasReplaceStopErrors(): boolean {
    return this.selectedStopsComponent?.hasReplaceStopErrors() ?? false;
  }

  public hasEffectedRoutesErrors(): boolean {
    return this.selectedStopsComponent?.hasEffectedRoutesErrors() ?? false;
  }

  public hasStopAreaErrors(): boolean {
    return this.selectedStopsComponent?.hasStopAreaErrors() ?? false;
  }

  public markStopAreasTouched(): void {
    this.selectedStopsComponent?.markStopAreasTouched();
  }

  public hasStopAreaMismatchErrors(): boolean {
    return this.selectedStopsComponent?.hasStopAreaMismatchErrors() ?? false;
  }

}
